import { SamdOrderRepository } from '@samd/api/order_repository';
import { GraphqlClient } from 'src/api/graphql_client';
import { GraphqlOrderRepository } from 'src/api/graphql_order_repository';
import type { DownloadReportResult } from 'src/api/order_repository';
import {
  CreatePatientInput,
  PatientBaseFieldsFragment,
  SpecimenType,
  SubmitOrderInput,
  SamdOrderDetailFieldsFragment,
  DownloadSlideImageDocument,
  DownloadSlideImageQuery,
  DownloadClinicalDecisionSupportDocument,
  DownloadClinicalDecisionSupportQuery,
  DownloadCombinedReportDocument,
  DownloadCombinedReportQuery,
  DownloadReportErrorCode,
  ResubmitOrderInput,
  UpdateOrderInput,
  CreateOrderInput,
  SamdOrdersQuery,
  SamdOrdersQueryVariables,
  SamdOrdersDocument,
  CreateSamdOrderMutation,
  CreateSamdOrderDocument,
  UpdateSamdOrderMutation,
  UpdateSamdOrderDocument,
  SubmitSamdOrderMutationVariables,
  SubmitSamdOrderMutation,
  SubmitSamdOrderDocument,
  ResubmitSamdOrderMutationVariables,
  ResubmitSamdOrderMutation,
  ResubmitSamdOrderDocument,
  SamdOrderDocument,
  SamdOrderQuery,
  CreateSamdMultipartUploadDocument,
  CreateSamdMultipartUploadMutation,
  DownloadTrrDocument,
  DownloadTrrQuery,
  SlideImageScannerType,
  UploadedFileCategory,
  SlideMetadataInputType,
} from 'src/types';
import { DistributionPlatform, Region } from 'src/types/env';

/**
 * Implementation of the Order Repository for the Samd platform on a GraphQL backend.
 */
export class SamdGraphqlOrderRepository
  extends GraphqlOrderRepository<SamdOrderDetailFieldsFragment>
  implements SamdOrderRepository
{
  readonly _platform = DistributionPlatform.Samd;

  constructor(
    graphqlClient: GraphqlClient,
    private readonly region: Region,
  ) {
    super(graphqlClient);
  }

  async getOrders(
    ordersQueryVariables: SamdOrdersQueryVariables,
  ): Promise<SamdOrdersQuery> {
    const pageIndex = Math.max(0, ordersQueryVariables.page - 1);
    const response = await this._graphqlClient.request<SamdOrdersQuery>(
      SamdOrdersDocument,
      {
        ...ordersQueryVariables,
        page: pageIndex,
      },
    );

    return response;
  }

  async getOrderById(
    id: string,
  ): Promise<SamdOrderDetailFieldsFragment | null> {
    const response = await this._graphqlClient.request<SamdOrderQuery>(
      SamdOrderDocument,
      {
        orderId: id,
      },
    );

    return response.order ?? null;
  }

  async create(
    input: CreateOrderInput,
  ): Promise<SamdOrderDetailFieldsFragment> {
    const mutationInput: Partial<CreateOrderInput> = {
      specimenType: input.specimenType,
      externalId: input.externalId,
      patientAge: input.patientAge,
      orderingPathway: input.orderingPathway,
      startedAt: input.startedAt,
    };

    if (input.specimenType === SpecimenType.ProstateBiopsy) {
      mutationInput.primaryGleasonScore = input.primaryGleasonScore;
      mutationInput.secondaryGleasonScore = input.secondaryGleasonScore;
      mutationInput.tStage = input.tStage;

      if ([Region.UK, Region.EU, Region.SG].includes(this.region)) {
        mutationInput.nccnRiskCategory = input.nccnRiskCategory;
        mutationInput.psa = input.psa;
      }
    } else if (input.specimenType === SpecimenType.BreastBiopsy) {
      mutationInput.nStage = input.nStage;
      mutationInput.tumorSize = input.tumorSize;
    } else if (input.specimenType === SpecimenType.ProstatePostRp) {
      mutationInput.primaryGleasonScore = input.primaryGleasonScore;
      mutationInput.secondaryGleasonScore = input.secondaryGleasonScore;
      mutationInput.psa = input.psa;
      mutationInput.tStage = input.tStage;
      mutationInput.surgicalMargin = input.surgicalMargin;
    } else {
      throw new Error('Unsupported order type');
    }

    if (input.patient) {
      mutationInput.patient = input.patient as CreatePatientInput;
    }

    const response = await this._graphqlClient.request<CreateSamdOrderMutation>(
      CreateSamdOrderDocument,
      {
        order: mutationInput,
      },
    );

    return response.createOrder!.order!;
  }

  async update(
    order: SamdOrderDetailFieldsFragment,
  ): Promise<SamdOrderDetailFieldsFragment> {
    // Only include fields that are not null or undefined
    const input: Partial<UpdateOrderInput> = {
      id: order.id,
      specimenType: order.specimenType ?? undefined,
      externalId: order.externalId,
      patientAge: order.patientAge,
    };

    // Conditionally include fields based on order type
    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.tStage = order.tStage;

      if ([Region.UK, Region.EU, Region.SG].includes(this.region)) {
        input.nccnRiskCategory = order.nccnRiskCategory;
        input.psa = order.psa;
      }
    } else if (order.specimenType === SpecimenType.BreastBiopsy) {
      input.nStage = order.nStage;
      input.tumorSize = order.tumorSize;
    } else if (order.specimenType === SpecimenType.ProstatePostRp) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.surgicalMargin = order.surgicalMargin;
    } else {
      throw new Error('Unsupported order type');
    }

    const patientInput = buildSamdPatientInput(order.patient);
    if (patientInput) {
      input.patient = patientInput;
    }

    const response = await this._graphqlClient.request<UpdateSamdOrderMutation>(
      UpdateSamdOrderDocument,
      {
        order: input,
      },
    );

    return response.updateOrder!.order!;
  }

  async submit(
    order: SamdOrderDetailFieldsFragment,
  ): Promise<SamdOrderDetailFieldsFragment> {
    // Only include fields that are not null or undefined
    const input: Partial<SubmitOrderInput> = {
      id: order.id,
      specimenType: order.specimenType ?? undefined,
      externalId: order.externalId ?? undefined,
      patientAge: order.patientAge ?? undefined,
    };

    // Conditionally include fields based on order type and region
    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.tStage = order.tStage;

      if ([Region.UK, Region.EU, Region.SG].includes(this.region)) {
        input.nccnRiskCategory = order.nccnRiskCategory;
        input.psa = order.psa;
      }
    } else if (order.specimenType === SpecimenType.BreastBiopsy) {
      input.nStage = order.nStage;
      input.tumorSize = order.tumorSize;
    } else if (order.specimenType === SpecimenType.ProstatePostRp) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.surgicalMargin = order.surgicalMargin;
    } else {
      throw new Error('Unsupported order type');
    }

    const patientInput = buildSamdPatientInput(order.patient);
    if (patientInput) {
      input.patient = patientInput;
    }

    const variables: SubmitSamdOrderMutationVariables = {
      input: input as SubmitOrderInput,
    };

    const response = await this._graphqlClient.request<SubmitSamdOrderMutation>(
      SubmitSamdOrderDocument,
      variables,
    );

    return response.submitOrder!.order!;
  }

  async resubmit(
    order: SamdOrderDetailFieldsFragment,
  ): Promise<SamdOrderDetailFieldsFragment> {
    // Only include fields that are not null or undefined
    const input: Partial<ResubmitOrderInput> = {
      id: order.id!,
      specimenType: order.specimenType ?? undefined,
      externalId: order.externalId!,
      patientAge: order.patientAge ?? undefined,
    };

    // Conditionally include fields based on order type
    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.tStage = order.tStage;

      if ([Region.UK, Region.EU, Region.SG].includes(this.region)) {
        input.nccnRiskCategory = order.nccnRiskCategory;
        input.psa = order.psa;
      }
    } else if (order.specimenType === SpecimenType.BreastBiopsy) {
      input.nStage = order.nStage;
      input.tumorSize = order.tumorSize;
    } else if (order.specimenType === SpecimenType.ProstatePostRp) {
      input.primaryGleasonScore = order.primaryGleasonScore;
      input.secondaryGleasonScore = order.secondaryGleasonScore;
      input.psa = order.psa;
      input.tStage = order.tStage;
      input.surgicalMargin = order.surgicalMargin;
    } else {
      throw new Error('Unsupported order type');
    }

    const patientInput = buildSamdPatientInput(order.patient);
    if (patientInput) {
      input.patient = patientInput;
    }

    const variables: ResubmitSamdOrderMutationVariables = {
      input: input as ResubmitOrderInput,
    };

    const response =
      await this._graphqlClient.request<ResubmitSamdOrderMutation>(
        ResubmitSamdOrderDocument,
        variables,
      );

    return response.resubmitOrder!.order!;
  }

  async downloadTRR(
    orderId: string,
    signed: boolean,
    _isDeidentified: boolean,
  ): Promise<DownloadReportResult> {
    const response = await this._graphqlClient.request<DownloadTrrQuery>(
      DownloadTrrDocument,
      {
        id: orderId,
        signed: signed,
      },
    );

    const result = response.downloadTRR;
    switch (result.__typename) {
      case 'TestResultReport':
        return { success: true, url: result.presignedUrl };
      case 'DownloadReportError':
        return { success: false, code: result.code };
    }
  }

  async downloadSlideImage(fileId: string): Promise<string> {
    const response = await this._graphqlClient.request<DownloadSlideImageQuery>(
      DownloadSlideImageDocument,
      {
        id: fileId,
      },
    );

    return response.fileDownloadUrl!;
  }

  async downloadClinicalDecisionSupport(
    orderId: string,
  ): Promise<DownloadReportResult> {
    const response =
      await this._graphqlClient.request<DownloadClinicalDecisionSupportQuery>(
        DownloadClinicalDecisionSupportDocument,
        { id: orderId },
      );

    return mapPresignedUrlDownloadResult(
      response.downloadClinicalDecisionSupport,
    );
  }

  async downloadCombinedReport(orderId: string): Promise<DownloadReportResult> {
    const response =
      await this._graphqlClient.request<DownloadCombinedReportQuery>(
        DownloadCombinedReportDocument,
        { id: orderId },
      );

    return mapPresignedUrlDownloadResult(response.downloadCombinedReport);
  }

  async createMultipartUpload(
    filename: string,
    category: UploadedFileCategory,
    orderId: string,
    countParts: number,
    signal?: AbortSignal,
    _slideMetadata?: SlideMetadataInputType,
    scannerType?: SlideImageScannerType,
  ): Promise<string> {
    const response =
      await this._graphqlClient.request<CreateSamdMultipartUploadMutation>(
        CreateSamdMultipartUploadDocument,
        {
          filename: filename,
          category: category,
          orderId: orderId,
          countParts: countParts,
          scannerType: scannerType ?? undefined,
        },
        undefined,
        signal,
      );

    return response.createMultipartUpload!.uploadedFile.id;
  }
}

function buildSamdPatientInput(
  patient: PatientBaseFieldsFragment | null,
): CreatePatientInput | undefined {
  if (
    !patient?.firstNamePhi &&
    !patient?.lastNamePhi &&
    !patient?.dateOfBirthPhi
  ) {
    return undefined;
  }

  return {
    firstNamePhi: patient.firstNamePhi ?? null,
    lastNamePhi: patient.lastNamePhi ?? null,
    dateOfBirthPhi: patient.dateOfBirthPhi ?? null,
  } as CreatePatientInput;
}

function mapPresignedUrlDownloadResult(
  result:
    | { __typename: 'ClinicalDecisionSupport'; presignedUrl: string }
    | { __typename: 'CombinedReport'; presignedUrl: string }
    | { __typename: 'DownloadReportError'; code: DownloadReportErrorCode },
): DownloadReportResult {
  if (result.__typename === 'DownloadReportError') {
    return { success: false, code: result.code };
  }

  return { success: true, url: result.presignedUrl };
}
